import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import * as Cesium from 'cesium';
import { ApiError, getSatelliteDetail, getSatellitePasses, getSatelliteRadio } from '../api/client';
import type { PassRecord, SatelliteDetail, SatelliteRadioTransmitter } from '../api/types';
import { getSharedObserver, type Observer } from '../observer/Observer';
import {
  buildScheduleEntry,
  currentPermissionState,
  iosNeedsHomeScreenInstall,
  readLeadMinutes,
  requestPermission,
} from '../notify/NotifyService';
import { getSharedScheduleStore, scheduleKey } from '../notify/NotifyScheduleStore';
import { PanelGesture } from './PanelGesture';
import type { SnapState } from './panelSnap';
import './FreshnessBadge';

/**
 * <sat-detail-panel
 *   .norad=${number | null}
 *   .getCurrentPosition=${(norad: number) => Cesium.Cartesian3 | null}>
 *
 * Right-rail panel showing the §10 sections we can populate in Phase 1:
 * Identity, Current state (live), Orbital elements, Raw data. Empty
 * Phase-2 fields (operator, country, mass, etc.) are surfaced as
 * "—" placeholders so the structure is visible.
 *
 * Closes via × button, Escape key, or .norad set to null.
 */
@customElement('sat-detail-panel')
export class SatDetailPanel extends LitElement {
  @property({ type: Number }) norad: number | null = null;

  /** Provided by <sat-app> — looks up live position from PointPrimitiveLayer. */
  @property({ attribute: false })
  getCurrentPosition: ((norad: number) => Cesium.Cartesian3 | null) | null = null;

  @state() private detail: SatelliteDetail | null = null;
  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private liveLat: number | null = null;
  @state() private liveLon: number | null = null;
  @state() private liveAlt: number | null = null;
  @state() private observer: Observer | null = null;
  @state() private passes: PassRecord[] = [];
  @state() private passesLoading = false;
  @state() private passesError: string | null = null;
  @state() private passesFromCache = false;
  @state() private ribbonOrbits = 1;
  /** Live elevation/azimuth from observer to selected satellite (degrees), or null if not computable. */
  @state() private liveElevationDeg: number | null = null;
  @state() private liveAzimuthDeg: number | null = null;

  @state() private radio: SatelliteRadioTransmitter[] = [];
  @state() private radioLoading = false;
  @state() private radioError: string | null = null;

  private liveTimer: number | null = null;
  private lastFetchedNorad: number | null = null;
  private observerUnsub: (() => void) | null = null;
  private lastPassKey = '';
  private passesToken = 0;
  private radioToken = 0;

  /** Phase 8 chunk 4 — mobile bottom-sheet gesture controller. */
  private gesture: PanelGesture | null = null;
  private readonly gripRef: Ref<HTMLDivElement> = createRef();

  /** Phase 8 chunk 5C — currently-armed pass notification keys. */
  @state() private armedNotifications: ReadonlySet<string> = new Set();
  /** Inline status line under the passes table after a notify action. */
  @state() private notifyStatus: string | null = null;
  private notifyUnsub: (() => void) | null = null;

  static styles = css`
    :host {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 380px;
      max-width: 100vw;
      background: var(--color-bg-overlay);
      border-left: 1px solid var(--color-border);
      backdrop-filter: blur(12px);
      color: var(--color-text);
      font-family: var(--font-body);
      font-size: 0.85rem;
      z-index: 20;
      transform: translateX(100%);
      transition: transform 0.25s ease;
      overflow-y: auto;
      pointer-events: auto;
    }
    :host([open]) {
      transform: translateX(0);
    }

    /* Phase 8 chunk 1 — reduced-motion respect. The slide-in stays
       structural (the panel still translates), but the easing drops
       to 0 ms so users sensitive to motion get an instant reveal
       instead of a 250 ms swoop. */
    @media (prefers-reduced-motion: reduce) {
      :host {
        transition: none;
      }
    }

    .header {
      position: sticky;
      top: 0;
      padding: 0.75rem 1rem;
      background: var(--color-bg-overlay);
      border-bottom: 1px solid var(--color-border);
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.5rem;
    }
    .header__name {
      font-family: var(--font-mono);
      font-size: 1rem;
      font-weight: 500;
      color: var(--color-text);
      line-height: 1.2;
    }
    .header__id {
      font-family: var(--font-mono);
      font-size: 0.75rem;
      color: var(--color-text-muted);
      margin-top: 0.2rem;
    }
    .close {
      background: transparent;
      border: 1px solid var(--color-border);
      color: var(--color-text-muted);
      width: 28px;
      height: 28px;
      border-radius: 4px;
      font-size: 1rem;
      line-height: 1;
      cursor: pointer;
      flex-shrink: 0;
    }
    .close:hover {
      color: var(--color-accent);
      border-color: var(--color-accent);
    }

    section {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--color-border);
    }
    section h2 {
      margin: 0 0 0.5rem;
      font-family: var(--font-mono);
      font-size: 0.8rem;
      font-weight: 500;
      color: var(--color-text-muted);
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.4rem 0.8rem;
    }
    .field {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
    }
    .field__label {
      font-size: 0.7rem;
      color: var(--color-text-dim);
      letter-spacing: 0.03em;
      text-transform: uppercase;
    }
    .field__value {
      font-family: var(--font-mono);
      color: var(--color-text);
      font-size: 0.85rem;
    }
    .field__value--muted {
      color: var(--color-text-muted);
      font-style: italic;
    }

    .badges {
      display: flex;
      gap: 0.4rem;
      flex-wrap: wrap;
      margin-bottom: 0.6rem;
    }
    .badge {
      display: inline-flex;
      padding: 0.1rem 0.45rem;
      font-family: var(--font-mono);
      font-size: 0.7rem;
      font-weight: 500;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      border-radius: 3px;
      border: 1px solid currentColor;
    }
    .badge--type    { color: var(--color-accent); }
    .badge--status  { color: var(--color-text-muted); }
    .badge--orbit   { color: var(--color-text-muted); }

    .tle {
      background: var(--color-bg);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      padding: 0.5rem;
      margin-top: 0.4rem;
      font-family: var(--font-mono);
      font-size: 0.7rem;
      color: var(--color-text);
      overflow-x: auto;
      white-space: pre;
      cursor: pointer;
    }
    .tle:hover { border-color: var(--color-accent); }
    .tle__hint {
      color: var(--color-text-dim);
      font-size: 0.7rem;
      margin-top: 0.3rem;
      font-style: italic;
    }

    .links {
      display: flex;
      gap: 0.4rem;
      flex-wrap: wrap;
      margin-top: 0.4rem;
    }
    .links a {
      color: var(--color-accent);
      font-family: var(--font-mono);
      font-size: 0.75rem;
      padding: 0.15rem 0.4rem;
      border: 1px solid var(--color-border);
      border-radius: 3px;
      text-decoration: none;
    }
    .links a:hover {
      border-color: var(--color-accent);
    }

    .epoch-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .loading,
    .error {
      padding: 2rem 1rem;
      text-align: center;
      color: var(--color-text-muted);
    }
    .error { color: var(--color-danger); }

    /* Phase 8 chunk 4 — drag-handle for the mobile bottom-sheet. Hidden
       on desktop; the @media block below flips display:block. The host's
       inner content is rendered AFTER the grip so the grip remains the
       sticky top edge a thumb can grab. */
    .grip {
      display: none;
      position: sticky;
      top: 0;
      width: 100%;
      padding: 0.5rem 0 0.4rem;
      background: var(--color-bg-overlay);
      border-bottom: 1px solid var(--color-border);
      touch-action: none;
      z-index: 2;
      cursor: ns-resize;
    }
    .grip__bar {
      width: 36px;
      height: 4px;
      margin: 0 auto;
      border-radius: 2px;
      background: var(--color-text-dim);
      opacity: 0.6;
    }

    @media (max-width: 700px) {
      :host {
        top: auto;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        /* The sheet now occupies the .globe-area height; translateY
           controls how much of it pokes above the viewport bottom.
           --mobile-translate-y is driven by PanelGesture; default 75%
           leaves a 25%-of-.globe-area peek strip visible. Closed state
           hides the sheet entirely (translateY 100%). */
        height: 100%;
        max-height: 100%;
        border-left: none;
        border-top: 1px solid var(--color-border);
        transform: translateY(100%);
      }
      :host([open]) {
        transform: translateY(var(--mobile-translate-y, 75%));
      }
      .grip { display: block; }
      /* The chunk-1 reduced-motion rule already drops the transition
         to 0 ms; gesture-driven drag also sets transition:none inline
         during a drag so the sheet tracks the finger. */
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleKeydown);
    this.observerUnsub = getSharedObserver().subscribe((o) => {
      this.observer = o;
      void this.maybeLoadPasses();
    });
    this.notifyUnsub = getSharedScheduleStore().subscribe((snap) => {
      this.armedNotifications = snap;
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeydown);
    this.stopLiveTimer();
    this.observerUnsub?.();
    this.observerUnsub = null;
    this.notifyUnsub?.();
    this.notifyUnsub = null;
    this.gesture?.destroy();
    this.gesture = null;
  }

  updated(): void {
    // PanelGesture wires the grip's pointer/touch listeners. The grip
    // is only rendered when a satellite is selected (render() returns
    // nothing before that), so wire-up happens lazily on the first
    // updated() pass that has the grip — not firstUpdated(), which
    // fires before any selection.
    const grip = this.gripRef.value;
    if (grip !== undefined && this.gesture === null) {
      this.gesture = new PanelGesture(this, grip, {
        onDismiss: () => this.dispatchClose(),
        onSnapChange: (state) => this.dispatchSnapChange(state),
      });
    }
  }

  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('norad')) {
      this.toggleAttribute('open', this.norad !== null);
      if (this.norad !== null && this.norad !== this.lastFetchedNorad) {
        // Each new selection lands at the mobile peek state per
        // docs/phase8.md § II row 7; previous drag position is reset.
        this.gesture?.reset();
        this.dispatchSnapChange('peek');
        void this.loadDetail(this.norad);
        void this.maybeLoadPasses();
        void this.loadRadio(this.norad);
      } else if (this.norad === null) {
        this.detail = null;
        this.lastFetchedNorad = null;
        this.stopLiveTimer();
        this.passes = [];
        this.passesError = null;
        this.lastPassKey = '';
        this.radio = [];
        this.radioError = null;
        this.gesture?.reset();
        this.dispatchSnapChange('peek');
      }
    }
  }

  private dispatchSnapChange(state: SnapState): void {
    this.dispatchEvent(
      new CustomEvent<{ state: SnapState }>('panel-snap-change', {
        detail: { state },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async maybeLoadPasses(): Promise<void> {
    if (this.norad === null || this.observer === null) {
      this.passes = [];
      this.passesError = null;
      this.lastPassKey = '';
      return;
    }
    const key = `${this.norad}:${this.observer.latitude.toFixed(3)}:${this.observer.longitude.toFixed(3)}`;
    if (key === this.lastPassKey && this.passes.length > 0) {
      return;
    }
    this.lastPassKey = key;
    const myToken = ++this.passesToken;
    this.passesLoading = true;
    this.passesError = null;
    try {
      const response = await getSatellitePasses(this.norad, {
        lat: this.observer.latitude,
        lon: this.observer.longitude,
        alt: this.observer.altitudeMeters,
        days: 7,
      });
      if (myToken !== this.passesToken) return;
      this.passes = response.data.slice(0, 5);
      this.passesFromCache = response.meta.from_cache;
    } catch (err) {
      if (myToken !== this.passesToken) return;
      this.passesError = err instanceof ApiError
        ? `Pass API error ${err.status}`
        : (err instanceof Error ? err.message : String(err));
      this.passes = [];
    } finally {
      if (myToken === this.passesToken) {
        this.passesLoading = false;
      }
    }
  }

  private async loadRadio(norad: number): Promise<void> {
    const myToken = ++this.radioToken;
    this.radioLoading = true;
    this.radioError = null;
    this.radio = [];
    try {
      const response = await getSatelliteRadio(norad);
      if (myToken !== this.radioToken) return;
      this.radio = response.data;
    } catch (err) {
      if (myToken !== this.radioToken) return;
      this.radioError = err instanceof ApiError
        ? `Radio API error ${err.status}`
        : (err instanceof Error ? err.message : String(err));
    } finally {
      if (myToken === this.radioToken) {
        this.radioLoading = false;
      }
    }
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.norad !== null) {
      this.dispatchClose();
    }
  };

  private async loadDetail(norad: number): Promise<void> {
    this.loading = true;
    this.error = null;
    this.detail = null;
    this.lastFetchedNorad = norad;

    try {
      const response = await getSatelliteDetail(norad);
      // Race guard — user may have selected another satellite while fetching
      if (this.norad !== norad) return;
      this.detail = response.data;
      this.startLiveTimer();
    } catch (err) {
      if (this.norad !== norad) return;
      this.error = err instanceof ApiError
        ? `API error ${err.status}: ${err.body || 'unknown'}`
        : (err instanceof Error ? err.message : String(err));
    } finally {
      if (this.norad === norad) {
        this.loading = false;
      }
    }
  }

  private startLiveTimer(): void {
    this.stopLiveTimer();
    this.tickLive(); // immediately
    this.liveTimer = window.setInterval(() => this.tickLive(), 500);
  }

  private stopLiveTimer(): void {
    if (this.liveTimer !== null) {
      window.clearInterval(this.liveTimer);
      this.liveTimer = null;
    }
    this.liveLat = this.liveLon = this.liveAlt = null;
  }

  private tickLive(): void {
    if (this.norad === null || this.getCurrentPosition === null) return;
    const pos = this.getCurrentPosition(this.norad);
    if (pos === null) {
      this.liveLat = this.liveLon = this.liveAlt = null;
      this.liveElevationDeg = this.liveAzimuthDeg = null;
      return;
    }
    const carto = Cesium.Cartographic.fromCartesian(pos);
    this.liveLat = Cesium.Math.toDegrees(carto.latitude);
    this.liveLon = Cesium.Math.toDegrees(carto.longitude);
    this.liveAlt = carto.height / 1000; // m → km

    // Phase 3 chunk 6A: live "above horizon" elevation/azimuth from
    // the observer.  Reuses the same look-angle math the chunk-6A
    // pass detector uses, but evaluated for "now" only.
    if (this.observer !== null) {
      const obsCart = Cesium.Cartesian3.fromDegrees(
        this.observer.longitude,
        this.observer.latitude,
        this.observer.altitudeMeters,
      );
      // ENU frame at the observer.  enu = enuTransform.inverse * world.
      const enuFromWorld = Cesium.Matrix4.inverseTransformation(
        Cesium.Transforms.eastNorthUpToFixedFrame(obsCart),
        new Cesium.Matrix4(),
      );
      const relWorld = Cesium.Cartesian3.subtract(pos, obsCart, new Cesium.Cartesian3());
      const enu = Cesium.Matrix4.multiplyByPointAsVector(enuFromWorld, relWorld, new Cesium.Cartesian3());
      const range = Cesium.Cartesian3.magnitude(enu);
      this.liveElevationDeg = range > 0 ? Cesium.Math.toDegrees(Math.asin(enu.z / range)) : null;
      // Azimuth: 0 = north, 90 = east. ENU has x=east, y=north.
      this.liveAzimuthDeg = Cesium.Math.toDegrees(Math.atan2(enu.x, enu.y));
      if (this.liveAzimuthDeg < 0) this.liveAzimuthDeg += 360;
    } else {
      this.liveElevationDeg = this.liveAzimuthDeg = null;
    }
  }

  private dispatchClose(): void {
    this.dispatchEvent(
      new CustomEvent('panel-close', { bubbles: true, composed: true }),
    );
  }

  private renderHorizonLine() {
    if (this.liveElevationDeg === null || this.liveAzimuthDeg === null) {
      return null;
    }
    const above = this.liveElevationDeg > 0;
    const compass = this.compassFromAzimuth(this.liveAzimuthDeg);
    return html`
      <p class="small" style="margin: 0 0 0.5rem; font-family: var(--font-mono); color: var(--color-text);">
        Above horizon now:
        <strong style="color: ${above ? 'var(--color-accent)' : 'var(--color-text-dim)'};">
          ${above ? 'Yes' : 'No'}
        </strong>
        <span style="color: var(--color-text-dim);">
          (${this.liveElevationDeg >= 0 ? '+' : ''}${this.liveElevationDeg.toFixed(1)}° el,
          ${this.liveAzimuthDeg.toFixed(0)}° ${compass} az)
        </span>
      </p>
    `;
  }

  private compassFromAzimuth(az: number): string {
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return dirs[Math.round(az / 45) % 8];
  }

  private setRibbonOrbits(orbits: number): void {
    if (orbits === this.ribbonOrbits) return;
    this.ribbonOrbits = orbits;
    this.dispatchEvent(
      new CustomEvent<{ orbits: number }>('ribbon-orbits-change', {
        detail: { orbits },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async copyTle(): Promise<void> {
    if (this.detail?.tle_current === null || this.detail?.tle_current === undefined) return;
    const text = `${this.detail.name}\n${this.detail.tle_current.line1}\n${this.detail.tle_current.line2}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore — older browsers / non-https origins may block clipboard
    }
  }

  private renderGrip() {
    return html`
      <div
        class="grip"
        role="separator"
        aria-label="Resize detail panel"
        aria-orientation="horizontal"
        ${ref(this.gripRef)}
      >
        <div class="grip__bar"></div>
      </div>
    `;
  }

  render() {
    if (this.norad === null) return nothing;
    // Keep the grip in a single stable template-literal slot across
    // every render branch so Lit reuses the same DOM node — otherwise
    // PanelGesture's listener attaches to a node that gets torn down
    // when the panel transitions from loading → loaded.
    return html`
      ${this.renderGrip()}
      ${this.renderBody()}
    `;
  }

  private renderBody() {
    if (this.loading) {
      return html`
        ${this.renderHeader('Loading…', null)}
        <div class="loading">Loading NORAD ${this.norad}…</div>
      `;
    }
    if (this.error !== null) {
      return html`
        ${this.renderHeader('Error', null)}
        <div class="error">${this.error}</div>
      `;
    }
    if (this.detail === null) return nothing;

    const d = this.detail;
    return html`
      ${this.renderHeader(d.name, d)}
      ${this.renderIdentity(d)}
      ${this.renderCurrentState(d)}
      ${this.renderVisibility(d)}
      ${d.tle_current !== null ? this.renderOrbital(d) : null}
      ${this.renderRadio()}
      ${d.tle_current !== null ? this.renderRaw(d) : null}
    `;
  }

  private renderRadio() {
    if (this.radioLoading && this.radio.length === 0) {
      return html`<section><h2>§ Radio</h2><p class="small" style="margin:0;">Loading transmitters…</p></section>`;
    }
    if (this.radioError !== null) {
      return html`<section><h2>§ Radio</h2><p class="small" style="margin:0; color: var(--color-warning);">${this.radioError}</p></section>`;
    }
    if (this.radio.length === 0) return null;
    return html`
      <section>
        <h2>§ Radio
          <span style="color: var(--color-text-dim); text-transform: none; letter-spacing: 0; font-weight: normal;">— SatNOGS DB</span>
        </h2>
        <table style="width: 100%; border-collapse: collapse; font-family: var(--font-mono); font-size: 0.78rem;">
          <thead>
            <tr style="text-align: left; color: var(--color-text-dim);">
              <th style="padding: 0.2rem 0.4rem 0.4rem 0;">Mode</th>
              <th style="padding: 0.2rem 0.4rem 0.4rem 0;">Description</th>
              <th style="padding: 0.2rem 0.4rem 0.4rem 0; text-align: right;">Downlink</th>
              <th style="padding: 0.2rem 0 0.4rem 0; text-align: right;">Uplink</th>
            </tr>
          </thead>
          <tbody>
            ${this.radio.map((t) => {
              const dl = formatFreq(t.downlink_low_hz, t.downlink_high_hz);
              const ul = formatFreq(t.uplink_low_hz, t.uplink_high_hz);
              const dim = t.alive ? '' : 'opacity: 0.5;';
              return html`
                <tr style="${dim}">
                  <td style="padding: 0.2rem 0.4rem 0.2rem 0;">${t.mode ?? '—'}</td>
                  <td style="padding: 0.2rem 0.4rem 0.2rem 0;" title=${t.description ?? ''}>
                    ${t.description ?? '—'}
                    ${t.alive ? null : html`<span style="color: var(--color-text-dim);"> (inactive)</span>`}
                  </td>
                  <td style="padding: 0.2rem 0.4rem 0.2rem 0; text-align: right;">${dl}</td>
                  <td style="padding: 0.2rem 0 0.2rem 0; text-align: right;">${ul}</td>
                </tr>
              `;
            })}
          </tbody>
        </table>
        <p class="small" style="margin: 0.4rem 0 0; color: var(--color-text-dim);">
          ${this.radio.length} transmitter${this.radio.length === 1 ? '' : 's'} · refreshed weekly from db.satnogs.org
        </p>
      </section>
    `;
  }

  private renderVisibility(d: SatelliteDetail) {
    if (d.tle_current === null) return null;
    if (this.observer === null) {
      return html`
        <section>
          <h2>§ Visibility from observer</h2>
          <p class="small" style="color: var(--color-text-dim); margin: 0;">
            Set your location with the 📍 pill in the top bar to see the next 5 passes.
          </p>
        </section>
      `;
    }
    if (this.passesLoading && this.passes.length === 0) {
      return html`<section><h2>§ Visibility from observer</h2><p class="small" style="margin:0;">Computing passes…</p></section>`;
    }
    if (this.passesError !== null) {
      return html`<section><h2>§ Visibility from observer</h2><p class="small" style="margin:0; color: var(--color-warning);">${this.passesError}</p></section>`;
    }
    if (this.passes.length === 0) {
      return html`
        <section>
          <h2>§ Visibility from observer</h2>
          <p class="small" style="margin: 0; color: var(--color-text-dim);">
            No passes ≥ 10° in the next 7 days from your location.
          </p>
        </section>
      `;
    }
    const obsLabel = this.observer.label?.split(',')[0]
      ?? `${this.observer.latitude.toFixed(2)}°, ${this.observer.longitude.toFixed(2)}°`;
    return html`
      <section>
        <h2>§ Visibility from observer
          <span style="color: var(--color-text-dim); text-transform: none; letter-spacing: 0; font-weight: normal;">— ${obsLabel}</span>
        </h2>
        ${this.renderHorizonLine()}
        <table class="passes" style="width: 100%; border-collapse: collapse; font-family: var(--font-mono); font-size: 0.8rem;">
          <thead>
            <tr style="text-align: left; color: var(--color-text-dim);">
              <th style="padding: 0.2rem 0.4rem 0.4rem 0;">Rise</th>
              <th style="padding: 0.2rem 0.4rem 0.4rem 0;">Peak</th>
              <th style="padding: 0.2rem 0.4rem 0.4rem 0;">Set</th>
              <th style="padding: 0.2rem 0.4rem 0.4rem 0; text-align: right;">Max el</th>
              <th style="padding: 0.2rem 0.4rem 0.4rem 0; text-align: right;" title="Visual magnitude — lower is brighter; <0 is naked-eye obvious">Mag</th>
              <th style="padding: 0.2rem 0 0.4rem 0; text-align: center;" title="Notify me before this pass" aria-label="Notify">🔔</th>
            </tr>
          </thead>
          <tbody>
            ${this.passes.map((p) => this.renderPassRow(p, d))}
          </tbody>
        </table>
        <p class="small" style="margin: 0.4rem 0 0; color: var(--color-text-dim);">
          UTC. Passes ≥ 10°. ${this.passesFromCache ? 'Cached.' : 'Fresh.'}
          ${this.renderIcsLink(d.norad_id)}
        </p>
        ${this.notifyStatus !== null ? html`
          <p class="small" style="margin: 0.3rem 0 0; color: var(--color-text);" data-notify-status>
            ${this.notifyStatus}
          </p>
        ` : nothing}
      </section>
    `;
  }

  private renderPassRow(p: PassRecord, d: SatelliteDetail) {
    const rise = new Date(p.rise_at);
    const set = new Date(p.set_at);
    const dateStr = rise.toISOString().slice(5, 10);
    const fmt = (date: Date) => date.toISOString().slice(11, 16);
    const mag = p.magnitude ?? null;
    return html`
      <tr>
        <td style="padding: 0.2rem 0.4rem 0.2rem 0;">${dateStr} ${fmt(rise)}</td>
        <td style="padding: 0.2rem 0.4rem 0.2rem 0;">${fmt(new Date(p.peak_at))}</td>
        <td style="padding: 0.2rem 0.4rem 0.2rem 0;">${fmt(set)}</td>
        <td style="padding: 0.2rem 0.4rem 0.2rem 0; text-align: right;">${p.max_elevation_deg.toFixed(0)}°</td>
        <td style="padding: 0.2rem 0.4rem 0.2rem 0; text-align: right; color: ${mag !== null && mag < 3 ? 'var(--color-accent)' : 'var(--color-text)'};">
          ${mag !== null ? mag.toFixed(1) : html`<span style="color: var(--color-text-dim);">—</span>`}
        </td>
        <td style="padding: 0.2rem 0 0.2rem 0; text-align: center;">
          ${this.renderNotifyButton(p, d)}
        </td>
      </tr>
    `;
  }

  /**
   * Phase 8 chunk 5C — per-row 🔔 toggle. Arms an in-page setTimeout
   * for `(rise_at - leadMinutes)`. Disabled when the platform can't
   * show notifications (iOS pre-PWA install, browser without API,
   * blocked permission state).
   */
  private renderNotifyButton(p: PassRecord, d: SatelliteDetail) {
    if (this.observer === null) return nothing;
    const key = scheduleKey(d.norad_id, p.rise_at);
    const armed = this.armedNotifications.has(key);
    const permission = currentPermissionState();
    const disabled = permission === 'denied' || permission === 'unsupported' || iosNeedsHomeScreenInstall();
    const title = armed
      ? 'Cancel the notification armed for this pass'
      : disabled
        ? 'Notifications unavailable in this browser — see §Settings'
        : permission === 'granted'
          ? `Notify me ${readLeadMinutes()} min before this pass`
          : 'Enable notifications and arm a reminder for this pass';
    return html`
      <button
        type="button"
        @click=${() => void this.handleNotifyClick(p, d)}
        ?disabled=${disabled}
        title=${title}
        aria-label=${title}
        aria-pressed=${armed ? 'true' : 'false'}
        data-pass-rise=${p.rise_at}
        style=${`
          background: transparent;
          border: 1px solid ${armed ? 'var(--color-accent)' : 'var(--color-border)'};
          color: ${armed ? 'var(--color-accent)' : disabled ? 'var(--color-text-dim)' : 'var(--color-text-muted)'};
          font-size: 0.85rem;
          padding: 0.05rem 0.3rem;
          border-radius: 3px;
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          opacity: ${disabled ? 0.5 : 1};
        `}
      >${armed ? '🔔' : '🔕'}</button>
    `;
  }

  private async handleNotifyClick(p: PassRecord, d: SatelliteDetail): Promise<void> {
    if (this.observer === null) return;
    const store = getSharedScheduleStore();
    if (store.isArmed(d.norad_id, p.rise_at)) {
      store.cancel(d.norad_id, p.rise_at);
      this.notifyStatus = 'Notification cancelled.';
      return;
    }
    let permission = currentPermissionState();
    if (permission === 'default') {
      permission = await requestPermission();
    }
    if (permission !== 'granted') {
      this.notifyStatus = permission === 'denied'
        ? 'Browser blocked notifications. Update the site permission to retry.'
        : permission === 'unsupported'
          ? "This browser doesn't support the Notifications API — try Chrome or Firefox."
          : 'Notifications not enabled. Re-click to try again.';
      return;
    }
    const leadMinutes = readLeadMinutes();
    const compass = this.compassFromAzimuth(p.peak_azimuth_deg ?? 0);
    const entry = buildScheduleEntry({
      norad: d.norad_id,
      satelliteName: d.name,
      riseAtIso: p.rise_at,
      maxElevationDeg: p.max_elevation_deg,
      peakCompass: compass,
      magnitude: p.magnitude ?? null,
      leadMinutes,
      observer: { latitude: this.observer.latitude, longitude: this.observer.longitude },
    });
    if (entry === null) {
      this.notifyStatus = "This pass is too soon — pick the next one (it fires lead-time before rise).";
      return;
    }
    store.arm(entry, leadMinutes);
    this.notifyStatus = `Notification armed: fires ${leadMinutes} min before rise.`;
  }

  /**
   * Phase 8 chunk 3B — `📅 Add to calendar` link rendered next to the
   * §Visibility table's metadata line. Visible only when an observer
   * is set (the parent already gates the §Visibility section on that).
   * Server-side endpoint (chunk 3A) does the ICS generation.
   */
  private renderIcsLink(norad: number) {
    if (this.observer === null) return nothing;
    const params = new URLSearchParams();
    params.set('lat', String(this.observer.latitude));
    params.set('lon', String(this.observer.longitude));
    if (this.observer.altitudeMeters !== 0) {
      params.set('alt', String(this.observer.altitudeMeters));
    }
    const href = `/api/v1/satellites/${norad}/passes.ics?${params.toString()}`;
    return html`
      <span style="margin-left: 0.5rem;">·</span>
      <a
        href=${href}
        download
        title="Download upcoming passes as a calendar file (Apple Calendar / Google Calendar / Outlook)"
        style="color: var(--color-accent); margin-left: 0.25rem;"
      >📅 Add to calendar</a>
    `;
  }

  private renderHeader(name: string, d: SatelliteDetail | null) {
    return html`
      <div class="header">
        <div>
          <div class="header__name">${name}</div>
          <div class="header__id">
            NORAD ${this.norad}${d?.intl_designator ? ` · ${d.intl_designator}` : ''}
          </div>
        </div>
        <button class="close" @click=${this.dispatchClose} title="Close (Esc)">×</button>
      </div>
    `;
  }

  private renderIdentity(d: SatelliteDetail) {
    return html`
      <section>
        <h2>§ Identity</h2>
        <div class="badges">
          <span class="badge badge--type">${d.object_type}</span>
          <span class="badge badge--status">${d.status}</span>
          <span class="badge badge--orbit">${d.orbit_class}</span>
        </div>
        <div class="grid">
          ${this.field('Operator', d.operator)}
          ${this.field('Country', d.country)}
          ${this.field('Launched',
            d.launch_date !== null && d.launch_site_code !== null
              ? `${d.launch_date} · ${d.launch_site_code}`
              : (d.launch_date ?? d.launch_site_code))}
          ${this.field('Launch vehicle', d.launch_vehicle)}
          ${this.field('Mass (kg)', d.mass_kg !== null ? d.mass_kg.toLocaleString() : null)}
          ${this.field('RCS (m²)', d.rcs_meters !== null ? d.rcs_meters.toFixed(2) : null)}
        </div>
        ${d.purposes.length > 0
          ? html`<p class="small" style="margin: 0.4rem 0 0; color: var(--color-text-muted);">
              Purposes: ${d.purposes.join(', ')}
            </p>`
          : null}
        ${d.decayed_at !== null
          ? html`<p class="small" style="margin: 0.4rem 0 0; color: var(--color-warning);">
              ⚠ Reentered: ${d.decayed_at}
            </p>`
          : null}
        ${this.renderLinks(d)}
      </section>
    `;
  }

  private renderCurrentState(_d: SatelliteDetail) {
    const lat = this.liveLat !== null ? `${this.liveLat.toFixed(3)}°` : '—';
    const lon = this.liveLon !== null ? `${this.liveLon.toFixed(3)}°` : '—';
    const alt = this.liveAlt !== null ? `${this.liveAlt.toFixed(1)} km` : '—';
    return html`
      <section>
        <h2>§ Current state <span style="color: var(--color-text-dim); text-transform: none; letter-spacing: 0; font-weight: normal;">— live</span></h2>
        <div class="grid">
          ${this.field('Latitude', lat)}
          ${this.field('Longitude', lon)}
          ${this.field('Altitude', alt)}
          ${this.field('Updated', '~2× / sec')}
        </div>
      </section>
    `;
  }

  private renderOrbital(d: SatelliteDetail) {
    const t = d.tle_current!;
    const orbitsOptions: Array<{ value: number; label: string }> = [
      { value: 0.5, label: '½' },
      { value: 1,   label: '1' },
      { value: 2,   label: '2' },
      { value: 3,   label: '3' },
    ];
    return html`
      <section>
        <h2>§ Orbital elements</h2>
        <div class="epoch-row" style="margin-bottom: 0.5rem;">
          <span style="font-family: var(--font-mono); font-size: 0.8rem;">${t.epoch}</span>
          <sat-freshness-badge epoch=${t.epoch}></sat-freshness-badge>
        </div>
        <div style="display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.6rem; font-family: var(--font-mono); font-size: 0.75rem; color: var(--color-text-dim);">
          <span>Ribbon:</span>
          ${orbitsOptions.map(
            (o) => html`<button
              @click=${() => this.setRibbonOrbits(o.value)}
              style=${`
                padding: 0.1rem 0.45rem;
                background: ${this.ribbonOrbits === o.value ? 'var(--color-accent)' : 'transparent'};
                color: ${this.ribbonOrbits === o.value ? 'var(--color-bg)' : 'var(--color-text)'};
                border: 1px solid var(--color-border);
                border-radius: 3px;
                font-family: var(--font-mono);
                font-size: 0.75rem;
                cursor: pointer;
              `}
            >${o.label}</button>`
          )}
          <span style="color: var(--color-text-dim);">orbit${this.ribbonOrbits === 1 ? '' : 's'}</span>
        </div>
        <div class="grid">
          ${this.field('Period', `${t.period_min.toFixed(2)} min`)}
          ${this.field('Inclination', `${t.inclination_deg.toFixed(4)}°`)}
          ${this.field('Eccentricity', t.eccentricity.toFixed(7))}
          ${this.field('Mean motion', `${t.mean_motion.toFixed(8)} rev/d`)}
          ${this.field('Perigee alt', `${t.perigee_km.toFixed(1)} km`)}
          ${this.field('Apogee alt', `${t.apogee_km.toFixed(1)} km`)}
          ${this.field('Semi-major', `${t.semimajor_km.toFixed(1)} km`)}
          ${this.field('B*', t.bstar.toExponential(4))}
          ${this.field('RAAN', `${t.raan_deg.toFixed(4)}°`)}
          ${this.field('Arg perigee', `${t.arg_perigee_deg.toFixed(4)}°`)}
          ${this.field('Mean anomaly', `${t.mean_anomaly_deg.toFixed(4)}°`)}
          ${this.field('Rev number', t.rev_number.toLocaleString())}
        </div>
      </section>
    `;
  }

  private renderRaw(d: SatelliteDetail) {
    const t = d.tle_current!;
    return html`
      <section>
        <h2>§ Raw data</h2>
        <pre class="tle" @click=${this.copyTle} title="Click to copy">${d.name}
${t.line1}
${t.line2}</pre>
        <div class="tle__hint">Click to copy the 3-line TLE set.</div>
        <div class="links">
          <a href=${`/api/v1/satellites/${this.norad}`} target="_blank" rel="noopener">JSON detail</a>
          <a href=${`/api/v1/satellites/${this.norad}/tle`} target="_blank" rel="noopener">JSON TLE</a>
        </div>
      </section>
    `;
  }

  private renderLinks(d: SatelliteDetail) {
    const links: Array<{ label: string; href: string }> = [];
    links.push({ label: 'N2YO', href: `https://www.n2yo.com/satellite/?s=${this.norad}` });
    links.push({ label: 'Heavens-Above', href: `https://heavens-above.com/orbit.aspx?satid=${this.norad}` });
    if (d.intl_designator) {
      links.push({ label: 'Gunter', href: `https://space.skyrocket.de/find_id.html?searchtxt=${encodeURIComponent(d.intl_designator)}` });
    }
    if (d.wikipedia_slug) {
      links.push({ label: 'Wikipedia', href: `https://en.wikipedia.org/wiki/${encodeURIComponent(d.wikipedia_slug)}` });
    }
    return html`
      <div class="links">
        ${links.map((l) => html`<a href=${l.href} target="_blank" rel="noopener">${l.label}</a>`)}
      </div>
    `;
  }

  private field(label: string, value: string | null) {
    if (value === null || value === '') {
      return html`
        <div class="field">
          <span class="field__label">${label}</span>
          <span class="field__value field__value--muted">—</span>
        </div>
      `;
    }
    return html`
      <div class="field">
        <span class="field__label">${label}</span>
        <span class="field__value">${value}</span>
      </div>
    `;
  }
}

/**
 * Format a SatNOGS-style Hz range as "145.825 MHz" or "145.825–146.000 MHz",
 * picking MHz / GHz so amateur (VHF/UHF) and Ku-band rows both read cleanly.
 */
function formatFreq(low: number | null, high: number | null): string {
  if (low === null) return '—';
  const fmt = (hz: number) => {
    const ghz = hz / 1e9;
    if (ghz >= 1) return `${ghz.toFixed(3)} GHz`;
    return `${(hz / 1e6).toFixed(3)} MHz`;
  };
  if (high === null || high === low) return fmt(low);
  return `${fmt(low)}–${fmt(high)}`;
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-detail-panel': SatDetailPanel;
  }
}
